import {
  Avatar,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Radio,
  RadioGroup,
  Skeleton,
  SkeletonCircle,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { SavedHolder } from 'api/checkoutpage/savedHolders';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DIALING_CODE_IRAQ } from 'utils/constants';
import { normalizeIraqiPhone } from 'utils/iraqiPhone';

type SavedHoldersSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Holders the buyer can pick, already filtered to ones the holder form accepts (`useSavedHolders`).
   * A failed fetch resolves to an empty list, so **error and "no saved holders" render the same
   * state** — this sheet is a shortcut, never a thing that can fail in the buyer's way.
   */
  holders: SavedHolder[];
  isLoading: boolean;
  onConfirm: (holder: SavedHolder) => void;
};

const SKELETON_ROWS = 3;

/**
 * List order is a presentation decision, so it lives here rather than in the API client
 * (`parseSavedHolders` preserves API order). Most-recently-used first: the buyer booking for the
 * same person again is the case this sheet exists for, and `lastUsedAt` is in the contract for it.
 */
const byMostRecentlyUsed = (holders: SavedHolder[]) =>
  [...holders].sort((a, b) => Date.parse(b.lastUsedAt) - Date.parse(a.lastUsedAt));

/**
 * The contract stores E.164; the card shows the dialing code beside the national digits, so the
 * sheet matches it. Holders that reach here are already phone-valid (`useSavedHolders` filters on
 * the same normalizer), and the raw value is the fallback rather than a blank row.
 */
const displayPhone = (phone: string) => {
  const normalized = normalizeIraqiPhone(phone);
  return normalized.ok ? `${DIALING_CODE_IRAQ} ${normalized.national}` : phone;
};

/**
 * The web/default contact source: a bottom sheet of holders the buyer has booked for before
 * (TZ-CO-5). Single-select + "Confirm", which collapses the holder card via the caller.
 *
 * Chakra's `Drawer` (bottom placement) is the sheet primitive rather than a bespoke modal — it
 * brings focus trapping, scroll locking, Escape/overlay dismissal and the portal for free.
 */
export const SavedHoldersSheet = ({
  isOpen,
  onClose,
  holders,
  isLoading,
  onConfirm,
}: SavedHoldersSheetProps) => {
  const { formatMessage } = useIntl();
  const [selectedId, setSelectedId] = useState<string>('');

  const orderedHolders = useMemo(() => byMostRecentlyUsed(holders), [holders]);
  const selectedHolder = orderedHolders.find((holder) => holder.id === selectedId);

  // The sheet is one shared instance reused by every holder card, so a selection made for ticket 1
  // must not still be checked when it reopens on ticket 2. Cleared on **both** transitions: doing
  // it only on open leaves the stale row checked (and Confirm enabled) for the first frame of the
  // next open, since the effect runs after that render.
  useEffect(() => {
    setSelectedId('');
  }, [isOpen]);

  const handleConfirm = () => {
    if (!selectedHolder) return;
    onConfirm(selectedHolder);
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='bottom'>
      <DrawerOverlay />
      <DrawerContent
        dir='rtl'
        borderTopRadius='24px'
        maxH='80vh'
        mx='auto'
        maxW={{ base: '100%', md: '480px' }}
      >
        {/* Chakra positions the close button physically top-right; RTL wants it top-left, and the
            header reserves matching inline-end room so the title never runs under it. */}
        <DrawerCloseButton
          top='16px'
          left='16px'
          right='auto'
          aria-label={formatMessage({ id: 'checkout.savedHolders.close', defaultMessage: 'Close' })}
        />
        <DrawerHeader pb='8px' pe='48px'>
          <VStack align='start' spacing='4px'>
            <Text textStyle='bodyLargeSemibold' color='dark.D900'>
              <FormattedMessage id='checkout.savedHolders.title' defaultMessage='Saved contacts' />
            </Text>
            <Text textStyle='bodySmallRegular' color='dark.D500' fontWeight='400'>
              <FormattedMessage
                id='checkout.savedHolders.subtitle'
                defaultMessage='Contacts you have booked for before'
              />
            </Text>
          </VStack>
        </DrawerHeader>

        <DrawerBody px='16px' py='8px' overflowY='auto'>
          {isLoading ? (
            <VStack spacing='8px' align='stretch' data-testid='saved-holders-loading'>
              {Array.from({ length: SKELETON_ROWS }, (_, row) => (
                <HStack key={row} spacing='12px' py='12px'>
                  <SkeletonCircle size='40px' />
                  <VStack align='stretch' spacing='6px' flex='1'>
                    <Skeleton height='12px' width='50%' />
                    <Skeleton height='10px' width='35%' />
                  </VStack>
                </HStack>
              ))}
            </VStack>
          ) : orderedHolders.length === 0 ? (
            <VStack spacing='4px' py='24px' textAlign='center'>
              <Text textStyle='bodySmallSemibold' color='dark.D900'>
                <FormattedMessage
                  id='checkout.savedHolders.emptyTitle'
                  defaultMessage='No saved contacts yet'
                />
              </Text>
              <Text textStyle='bodySmallRegular' color='dark.D500'>
                <FormattedMessage
                  id='checkout.savedHolders.emptyDescription'
                  defaultMessage="Enter the ticket holder's details manually and we'll save them for next time."
                />
              </Text>
            </VStack>
          ) : (
            <RadioGroup value={selectedId} onChange={setSelectedId}>
              <VStack spacing='0' align='stretch'>
                {orderedHolders.map((holder) => (
                  <Radio
                    key={holder.id}
                    value={holder.id}
                    // The whole row is the label, so tapping anywhere on it selects the holder.
                    // Reversed so the radio control sits on the far side from the name, per design.
                    w='100%'
                    py='12px'
                    flexDirection='row-reverse'
                    justifyContent='space-between'
                    colorScheme='green'
                    borderColor='dark.D150'
                  >
                    <HStack spacing='12px' minW='0'>
                      {/* Chakra derives the initials from the name — no bespoke helper needed.
                          `aria-hidden` because it also puts the full name in an `aria-label`, which
                          would otherwise be read into this radio's accessible name a second time,
                          on top of the name text below it. The avatar is decoration here. */}
                      <Avatar
                        name={holder.name}
                        size='sm'
                        bg='dark.D100'
                        color='dark.D700'
                        aria-hidden
                      />
                      <VStack align='end' spacing='2px' minW='0'>
                        <Text textStyle='bodySmallSemibold' color='dark.D900' noOfLines={1}>
                          {holder.name}
                        </Text>
                        <Text textStyle='bodySmallRegular' color='dark.D500' dir='ltr'>
                          {displayPhone(holder.phone)}
                        </Text>
                      </VStack>
                    </HStack>
                  </Radio>
                ))}
              </VStack>
            </RadioGroup>
          )}
        </DrawerBody>

        <DrawerFooter px='16px' pb='24px' pt='12px' borderTopWidth='1px' borderColor='dark.D100'>
          <Button
            type='button'
            bg='dark.D900'
            color='white'
            borderRadius='full'
            h='48px'
            w='100%'
            isDisabled={!selectedHolder}
            _hover={{ bg: 'dark.D800' }}
            _active={{ bg: 'dark.D700' }}
            onClick={handleConfirm}
          >
            <FormattedMessage id='checkout.savedHolders.confirm' defaultMessage='Confirm' />
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
